import { Link } from "@tanstack/react-router";
import { Menu, MessageCircle, User } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { LanguageToggle } from "@/components/site/LanguageToggle";
import { LogoMark } from "@/components/site/LogoMark";
import { useSupabaseUser } from "@/lib/use-supabase-user";
import { cn } from "@/lib/utils";

const nav = [
  { label: "Home", to: "/" },
  { label: "Rentals", to: "/rentals" },
  { label: "No Deposit", to: "/car-rental-no-deposit-bucharest" },
  { label: "Guides", to: "/blog" },
  { label: "Good to Know", to: "/good-to-know" },
  { label: "Privacy Policy", to: "/privacy-policy" },
] as const;

export function Header() {
  const { user, signOut } = useSupabaseUser();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={cn(
        "sticky top-0 z-50 border-b border-transparent bg-background/80 backdrop-blur transition-all",
        scrolled && "border-border shadow-card",
      )}
    >
      <div className="container-page grid h-14 grid-cols-[minmax(0,1fr)_auto] items-center gap-2 sm:h-16 sm:gap-4 lg:h-20">
        <Link to="/" className="flex min-w-0 items-center gap-2 sm:gap-2.5">
          <LogoMark className="size-8 shrink-0 sm:size-9" />
          <span className="min-w-0">
            <span className="block truncate font-display text-[13px] font-bold leading-tight sm:text-base">
              Car Rental Bucharest
            </span>
            <span className="hidden text-[11px] uppercase tracking-[0.2em] text-muted-foreground sm:block">
              carentals.ro
            </span>
          </span>
        </Link>

        <div className="flex items-center gap-1.5 sm:gap-3">

          <nav className="hidden items-center gap-1 md:flex">
            {nav.slice(0, 4).map((item) => (
              <Link
                key={item.to}
                to={item.to}
                activeOptions={{ exact: item.to === "/" }}
                activeProps={{ className: "text-foreground" }}
                inactiveProps={{ className: "text-muted-foreground" }}
                className="rounded-md px-3 py-2 text-sm font-semibold transition-colors hover:text-foreground"
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <a
            href="https://wa.me/40738792499"
            target="_blank"
            rel="noreferrer"
            className="hidden items-center gap-2 rounded-md px-3 py-2 text-sm font-semibold text-muted-foreground transition-colors hover:text-foreground lg:flex"
          >
            <MessageCircle className="size-4 text-gold" />
            WhatsApp
          </a>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <User className="size-4" />
                  <span className="max-w-24 truncate">{user.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem asChild>
                  <Link to="/portal">My bookings</Link>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => void signOut()}>Log out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="hidden items-center gap-2 sm:flex">
              <Button asChild variant="ghost" size="sm">
                <Link to="/auth" search={{ redirect: "/portal" }}>Login</Link>
              </Button>
              <Button asChild size="sm">
                <Link to="/auth" search={{ redirect: "/portal" }}>Register</Link>
              </Button>
            </div>
          )}

          <LanguageToggle />

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="icon" className="md:hidden" aria-label="Open menu">
                <Menu className="size-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <SheetTitle className="font-display">Menu</SheetTitle>
              <nav className="mt-6 flex flex-col gap-1">
                {nav.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setOpen(false)}
                    className="rounded-md px-3 py-2.5 text-sm font-semibold text-muted-foreground hover:bg-secondary hover:text-foreground"
                  >
                    {item.label}
                  </Link>
                ))}
              </nav>
              {user ? (
                <div className="mt-6">
                  <Button asChild className="w-full" onClick={() => setOpen(false)}>
                    <Link to="/portal">My bookings</Link>
                  </Button>
                </div>
              ) : (
                <div className="mt-6 flex flex-col gap-2">
                  <Button asChild variant="outline" onClick={() => setOpen(false)}>
                    <Link to="/auth" search={{ redirect: "/portal" }}>Login</Link>
                  </Button>
                  <Button asChild onClick={() => setOpen(false)}>
                    <Link to="/auth" search={{ redirect: "/portal" }}>Register</Link>
                  </Button>
                </div>
              )}
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
